import { cva, type VariantProps } from 'class-variance-authority'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
	ComboBox as AriaComboBox,
	Button,
	Input,
	type Key,
	ListBox,
	ListBoxItem,
	Popover,
	Text,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * One selectable option. `value` is the stable code stored in form state;
 * `label` is the human text shown + searched; `description` (optional) renders
 * as a muted second line — used e.g. by asset-type, whose codes carry a
 * definition designers pick by.
 */
export interface ComboboxOption {
	value: string
	label: string
	description?: string
	/** Hierarchy depth — indents the option (0 = top level). */
	depth?: number
}

/**
 * Filter a hierarchy-ordered option list without breaking the hierarchy.
 *
 * A plain "contains" filter is wrong for a tree: searching "LATAM" returns the
 * region alone, hiding the countries the user is actually reaching for, and a
 * match deep in the tree loses the ancestors that gave it meaning ("Beagle" with
 * no "Dog" above it). So a kept option is one that either matches itself, has a
 * matching ANCESTOR (searching a parent reveals its subtree), or has a matching
 * DESCENDANT (a deep match keeps its context).
 *
 * Ancestry comes from `depth` + input order rather than a parent link: the list
 * arrives parent-before-child, so an option's parent is the nearest preceding
 * option of lower depth. Options without `depth` are flat, and this reduces to
 * the ordinary substring filter.
 */
export function filterHierarchicalOptions(options: ComboboxOption[], query: string): ComboboxOption[] {
	const text = query.trim().toLowerCase()
	if (!text) return options

	const matches = options.map((o) => `${o.label} ${o.description ?? ''}`.toLowerCase().includes(text))
	const keep = [...matches]

	// Ancestors of a match, and any option under a matched ancestor.
	const ancestors: number[] = []
	options.forEach((option, i) => {
		const depth = option.depth ?? 0
		ancestors.length = depth
		if (matches[i]) for (const a of ancestors) if (a !== undefined) keep[a] = true
		if (ancestors.some((a) => a !== undefined && matches[a])) keep[i] = true
		ancestors[depth] = i
	})

	return options.filter((_, i) => keep[i])
}

export const comboboxInputVariants = cva(
	// `group-data-[invalid]`: React Aria marks the ComboBox wrapper, not the input,
	// so without this an invalid field is announced to a screen reader but looks
	// identical to a valid one.
	'w-full rounded-ds-md border border-ds-border bg-ds-background text-ds-text placeholder:text-ds-text-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50 group-data-[invalid]:border-ds-danger group-data-[invalid]:focus-visible:ring-ds-danger',
	{
		variants: {
			size: {
				sm: 'h-8 pl-2 pr-14 text-ds-sm',
				md: 'h-10 pl-3 pr-16 text-ds-base',
				lg: 'h-12 pl-4 pr-16 text-ds-lg',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

export interface ComboboxProps extends VariantProps<typeof comboboxInputVariants> {
	options: ComboboxOption[]
	/** Selected option value (controlled). `undefined`/`null` = nothing chosen. */
	value?: string | null
	/** Fires with the chosen value, or `undefined` when cleared. */
	onChange: (value: string | undefined) => void
	/** Fires alongside `onChange` with the chosen option's human label (or `undefined`). */
	onLabelChange?: (label: string | undefined) => void
	placeholder?: string
	isDisabled?: boolean
	/**
	 * Marks the value invalid. React Aria puts `aria-invalid` on the input, so a
	 * form's error state reaches assistive tech rather than living only in the
	 * border colour.
	 */
	isInvalid?: boolean
	/**
	 * Marks the field required. React Aria does not put `aria-required` on the
	 * input by itself, so without this a required combobox is announced exactly
	 * like an optional one — the visible `*` is `aria-hidden` decoration.
	 */
	isRequired?: boolean
	/** Show a clear ("×") button when a value is selected. Defaults to true. */
	isClearable?: boolean
	/**
	 * More options exist beyond `options` on a paged remote source. Renders a
	 * "Load more" row at the end of the list; ignored without `onLoadMore`.
	 */
	hasMore?: boolean
	/** Fetch the next page. The caller appends to `options`; this atom never fetches. */
	onLoadMore?: () => void
	/** A page is in flight — the load-more row says so and stops accepting presses. */
	isLoadingMore?: boolean
	/** The options are still loading: an empty list says "Loading…", not "No matches". */
	isLoading?: boolean
	className?: string
	/** Accessible name — REQUIRED (there is no visible label inside this atom). */
	'aria-label'?: string
	'aria-labelledby'?: string
}

/**
 * Searchable single-select combobox built on React Aria's `ComboBox`
 * (keyboard nav, type-ahead, screen-reader support, blur-reverts-to-selection
 * — all for free). Filters an **in-memory** option list — no server search —
 * because ATLAS forms bind to the axis registry, which is already loaded
 * client-side. A paged remote source grows that in-memory list through
 * `onLoadMore`; a genuinely search-backed variant (mirroring the flight-control
 * `AutocompleteField`) belongs beside this atom rather than inside it.
 *
 * Design decisions (see the ComboBox best-practices research):
 * - Controls ONLY `selectedKey`/`onSelectionChange`; the input text is left
 *   **uncontrolled** so React Aria renders the selected label and reverts junk
 *   typing on blur. Mirroring the key into `inputValue` is the classic
 *   "input shows a raw code" bug — deliberately avoided.
 * - Filtering is owned here (`filterHierarchicalOptions`) rather than by React
 *   Aria's built-in "contains", because a value tree must keep a match's
 *   ancestors and descendants. The collection is memoized so an inline `.map()`
 *   from the caller can't trigger re-render loops.
 * - `onLabelChange` hands back the human label so callers storing codes can
 *   render "Bravecto" instead of a code and keep it stable across renames.
 * - `onLoadMore` appends a row at the end of the list for a paged remote source
 *   (see `hasMore`). The atom stays in-memory: the caller owns fetching and just
 *   grows `options`.
 *
 * @example
 * ```tsx
 * <Combobox aria-label="Brand" options={brands} value={brand} onChange={setBrand} />
 * ```
 */
export function Combobox({
	options,
	value,
	onChange,
	onLabelChange,
	placeholder = 'Search…',
	isDisabled,
	isInvalid,
	isRequired,
	isClearable = true,
	hasMore,
	onLoadMore,
	isLoadingMore,
	isLoading = false,
	size,
	className,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledby,
}: ComboboxProps) {
	// Stabilise the collection: React Aria warns / re-renders badly on a fresh
	// array identity each render. Keyed on option contents, not reference.
	const items = useMemo(() => options, [JSON.stringify(options)])
	// Filtering is owned here, not left to React Aria's built-in "contains": a
	// tree needs its ancestors and descendants kept alongside a match.
	//
	// Only a keystroke narrows the list. Selecting an option also writes that
	// option's label into the input, and treating that as a query would leave the
	// menu showing the single row just picked — so the query is cleared whenever
	// the change came from anything but typing.
	const [query, setQuery] = useState('')
	const visibleItems = useMemo(() => filterHierarchicalOptions(items, query), [items, query])

	const handleSelection = (key: Key | null) => {
		setQuery('')
		if (key === null) {
			onChange(undefined)
			onLabelChange?.(undefined)
			return
		}
		const picked = options.find((o) => o.value === String(key))
		onChange(picked?.value)
		onLabelChange?.(picked?.label)
	}

	return (
		<AriaComboBox
			items={visibleItems}
			selectedKey={value ?? null}
			onSelectionChange={handleSelection}
			onInputChange={setQuery}
			// Reopening on the trigger/arrow shows the whole list again, never the
			// residue of whatever is currently selected.
			onOpenChange={(isOpen, reason) => {
				if (isOpen && reason !== 'input') setQuery('')
			}}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledby}
			className={cn('group relative w-full', className)}
			menuTrigger="focus"
			allowsEmptyCollection
			// Filtering already happened in `visibleItems`; the default filter would
			// then drop the ancestors/descendants deliberately kept alongside a match.
			defaultFilter={() => true}
		>
			<div className="relative">
				{/* `aria-required` on the input itself: React Aria's ComboBox does not
				    forward `isRequired` to it, and the visible `*` is aria-hidden. */}
				<Input
					placeholder={placeholder}
					aria-required={isRequired || undefined}
					className={comboboxInputVariants({ size })}
				/>
				<div className="absolute right-0 top-0 flex h-full items-center pr-1.5 text-ds-text-subtle">
					{isClearable && value ? (
						<Button
							slot={null}
							aria-label="Clear selection"
							onPress={() => handleSelection(null)}
							className="flex items-center px-1 hover:text-ds-text"
						>
							<X className="h-3.5 w-3.5" aria-hidden="true" />
						</Button>
					) : null}
					<Button aria-label="Show options" className="flex items-center px-1">
						<ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
					</Button>
				</div>
			</div>
			<Popover className="w-(--trigger-width) rounded-ds-md border border-ds-border bg-ds-background shadow-ds-lg">
				<ListBox
					className="max-h-60 overflow-auto p-1 outline-none"
					renderEmptyState={() => (
						<p className="px-2 py-1.5 text-ds-sm text-ds-text-subtle">{isLoading ? 'Loading…' : 'No matches'}</p>
					)}
				>
					{(item: ComboboxOption) => (
						<ListBoxItem
							id={item.value}
							textValue={item.label}
							style={item.depth ? { paddingLeft: `${0.5 + item.depth}rem` } : undefined}
							className="group flex cursor-pointer items-start gap-2 rounded-ds-sm px-2 py-1.5 text-ds-sm text-ds-text outline-none data-[focused]:bg-ds-surface data-[selected]:font-medium"
						>
							<Check
								className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ds-primary opacity-0 group-data-[selected]:opacity-100"
								aria-hidden="true"
							/>
							<span className="min-w-0">
								<Text slot="label" className="block truncate">
									{item.depth ? (
										// Explicit guide, not indentation alone: the filtered list hides a
										// child's parent, and the closed input shows no indent at all.
										<span className="mr-1 select-none text-ds-text-subtle" aria-hidden="true">
											└─
										</span>
									) : null}
									{item.label}
								</Text>
								{item.description ? (
									<Text slot="description" className="block truncate text-ds-xs text-ds-text-subtle">
										{item.description}
									</Text>
								) : null}
							</span>
						</ListBoxItem>
					)}
				</ListBox>
				{/* Outside the ListBox on purpose: inside, it would be a focusable option
				    that a keyboard user could "select" as a value. */}
				{hasMore && onLoadMore ? (
					<div className="border-ds-border border-t p-1">
						<Button
							slot={null}
							isDisabled={isLoadingMore}
							onPress={onLoadMore}
							className="w-full rounded-ds-sm px-2 py-1.5 text-left text-ds-sm text-ds-text-subtle hover:bg-ds-surface hover:text-ds-text disabled:cursor-not-allowed"
						>
							{isLoadingMore ? 'Loading…' : `↓ Load more (${options.length} shown)`}
						</Button>
					</div>
				) : null}
			</Popover>
		</AriaComboBox>
	)
}
